import { useEffect, useState } from "react";

import {
  animate as motionAnimate,
  useMotionValue,
  useTransform,
} from "framer-motion";

import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { normalizeAnimationSpeed } from "src/copilot/utils";

type TextContentProps = {
  content: string;
  delay: number;
  animate?: boolean;
  inline?: boolean;
};

export const CopilotTextContent: React.FC<TextContentProps> = ({
  content,
  delay = 0,
  animate = false,
  inline = false,
}) => {
  const [displayTextValue, setDisplayTextValue] = useState("");
  const count = useMotionValue(0);
  useEffect(() => {
    if (animate) {
      const duration = normalizeAnimationSpeed(content.length);
      const controls = motionAnimate(count, content.length, {
        duration,
        delay,
        ease: "linear",
      });
      return controls.stop;
    }
  }, [content.length, count, delay, animate]);

  const rounded = useTransform(count, (latest) => Math.round(latest));
  const displayText = useTransform(rounded, (latest) =>
    content.slice(0, latest),
  );

  useEffect(() => {
    displayText.on("change", (value) => {
      setDisplayTextValue(value);
    });

    return () => displayText.destroy();
  }, [displayText]);

  const textToRender = animate ? displayTextValue : content;

  // When inline, render as a simple span to flow naturally with mention pills
  const animationFinishesAfter = Math.ceil(
    delay + normalizeAnimationSpeed(content.length),
  );

  if (inline) {
    return (
      <span
        className="ql-editor whitespace-pre-wrap"
        data-loc={`copilot-text-finishes-after-${animationFinishesAfter}`}
      >
        {textToRender}
      </span>
    );
  }

  return (
    <TextEditor
      dataLoc={`copilot-text-finishes-after-${animationFinishesAfter}`}
      value={textToRender}
      inline
      readOnly
    />
  );
};
